import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import ReferralCodeCard from "../../components/features/ReferAndEarn/ReferralCodeCard";
import ReferralStats from "../../components/features/ReferAndEarn/ReferralStats";
// import RewardHistory from '../../services/api/referral.service/RewardHistory';
import ReferralForm from "../../components/features/ReferAndEarn/ReferralForm";
import LoadingSpinner from "../../components/features/ReferAndEarn/LoadSpinner";
import referralService from "../../services/api/referral.service";
import PropTypes from "prop-types";

const ReferralEarnPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [referralStats, setReferralStats] = useState({
    pendingReferrals: 0,
    qualifiedReferrals: 0,
    completedReferrals: 0,
  });
  const [rewardStats, setRewardStats] = useState({
    pendingRewards: 0,
    paidRewards: 0,
    totalEarned: 0,
  });
  const [referralHistory, setReferralHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();
  const location = useLocation();

  const metadata = {
    title: "Join my Private Investment Circle | InvestHive Referral",
    description: "Join our exclusive investment network on InvestHive. Access institutional-grade unlisted equity opportunities and preferred member terms by joining my private circle.",
    image: "/og-image.jpg", // Professional fallback
    url: window.location.origin + "/referral"
  };

  useEffect(() => {
    // Store pending referral code if present in URL
    const params = new URLSearchParams(location.search);
    const refCode = params.get("ref");
    if (refCode) {
      localStorage.setItem("pendingReferralCode", refCode);
      // Remove it from URL so it doesn't stay there forever
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check if user is authenticated
    const token = localStorage.getItem("authToken");
    if (!token) {
      // Redirect to /signup instead of /Signin-Signup for referrals
      navigate("/signup", { state: { from: "/referral" } });
      return;
    }

    fetchReferralData();
  }, [navigate, location.search]);

  // In ReferralEarnPage.jsx where you fetch the data

  const fetchReferralData = async () => {
    try {
      setIsLoading(true);

      // Fetch referral stats
      const statsResponse = await referralService.getReferralStats();
      if (statsResponse.success) {
        // Set the referral code
        if (
          statsResponse.data.summary &&
          statsResponse.data.summary.referralCode
        ) {
          setReferralCode(statsResponse.data.summary.referralCode);
        }

        // Pass the entire data object to ReferralStats
        setReferralStats(statsResponse.data);
      }
    } catch (error) {
      toast.error("Failed to load referral data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const generateReferralCode = async () => {
    try {
      setIsLoading(true);
      const response = await referralService.generateReferralCode();

      if (response.success) {
        setReferralCode(response.data.referralCode);
        toast.success("Referral code generated successfully!");
      }
    } catch (error) {
      toast.error("Failed to generate referral code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied to clipboard!");
  };

  const shareReferral = (platform) => {
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
    let shareUrl = "";

    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=Join me on this amazing platform! Use my referral code: ${referralCode} or sign up here: ${referralLink}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=Join me on this amazing platform! Use my referral code: ${referralCode} or sign up here: ${referralLink}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          referralLink
        )}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=Join me on this amazing platform&body=Use my referral code: ${referralCode} or sign up here: ${referralLink}`;
        break;
      default:
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-gray-50 mt-10">
      <Helmet>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metadata.url} />
        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.description} />
        <meta property="og:image" content={metadata.image} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={metadata.url} />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.description} />
        <meta name="twitter:image" content={metadata.image} />
      </Helmet>
      <div className="container mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Tabs */}
          <div
            className="flex justify-center mb-8 border-b border-gray-200 overflow-x-auto"
            style={{
              msOverflowStyle: "none" /* IE and Edge */,
              scrollbarWidth: "none" /* Firefox */,
            }}
          >
            {/* Hide scrollbar for Chrome, Safari and Opera */}
            <style>{`
            div.overflow-x-auto::-webkit-scrollbar {
              display: none;
            }
          `}</style>
            <div className="flex space-x-2 sm:space-x-6 whitespace-nowrap min-w-full sm:min-w-0">
              <button
                className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-sm sm:text-lg transition-colors duration-200 ease-in-out
                ${activeTab === "overview"
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-600 hover:text-primary"
                  }`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-sm sm:text-lg transition-colors duration-200 ease-in-out
                ${activeTab === "how-it-works"
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-600 hover:text-primary"
                  }`}
                onClick={() => setActiveTab("how-it-works")}
              >
                How It Works
              </button>
              {/* <button
              className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-sm sm:text-lg transition-colors duration-200 ease-in-out
                ${activeTab === 'history' ? 'text-primary border-b-2 border-primary' : 'text-gray-600 hover:text-primary'}`}
              onClick={() => setActiveTab('history')}
            >
              Referral History
            </button> */}
            </div>
          </div>
          {/* Overview Tab Content */}

          {activeTab === "overview" && (
            <div className="space-y-8">
              <ReferralCodeCard
                referralCode={referralCode}
                generateReferralCode={generateReferralCode}
                copyReferralLink={copyReferralLink}
                shareReferral={shareReferral}
                activeRewardAmount={referralStats.summary?.activeRewardAmount}
                minimumTransactionAmount={referralStats.summary?.activeConfig?.minimumTransactionAmount}
              />

              <div className="mt-8">
                <ReferralForm />
              </div>

              <div className="mb-8">
                <ReferralStats referralData={referralStats} />
              </div>
            </div>
          )}

          {/* How It Works Tab Content */}
          {activeTab === "how-it-works" && (
            <div className="bg-white rounded-xl shadow-lg p-8 py-20">
              <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">
                How Referrals Work
              </h2>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <div className="bg-gray-50 p-6 rounded-lg text-center relative">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto -mt-10 shadow-md">
                    <span className="text-xl font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-semibold mt-4 mb-2">
                    Generate Your Code
                  </h3>
                  <p className="text-gray-600">
                    Create your unique referral code to share with friends and
                    family.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg text-center relative">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto -mt-10 shadow-md">
                    <span className="text-xl font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-semibold mt-4 mb-2">
                    Share Your Code
                  </h3>
                  <p className="text-gray-600">
                    Share your referral code via social media, email, or direct
                    message.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg text-center relative">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto -mt-10 shadow-md">
                    <span className="text-xl font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-semibold mt-4 mb-2">
                    Friends Sign Up
                  </h3>
                  <p className="text-gray-600">
                    When friends sign up using your referral code, they become
                    your referrals.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg text-center relative">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto -mt-10 shadow-md">
                    <span className="text-xl font-bold">4</span>
                  </div>
                  <h3 className="text-xl font-semibold mt-4 mb-2">
                    Complete KYC & Invest
                  </h3>
                  <p className="text-gray-600">
                    Once referrals complete KYC and make an investment
                    {referralStats.summary?.activeConfig?.minimumTransactionAmount
                      ? ` of at least ₹${referralStats.summary.activeConfig.minimumTransactionAmount.toLocaleString("en-IN")}`
                      : ""}
                    , they become qualified.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg text-center relative">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto -mt-10 shadow-md">
                    <span className="text-xl font-bold">5</span>
                  </div>
                  <h3 className="text-xl font-semibold mt-4 mb-2">
                    Earn Rewards
                  </h3>
                  <p className="text-gray-600">
                    You earn rewards for each qualified referral credited to
                    your wallet.
                  </p>
                </div>
              </div>


            </div>
          )}

          {/* History Tab Content */}
          {activeTab === "history" && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-3xl font-bold mb-8 text-gray-900">
                Your Referral History
              </h2>
              {/* <RewardHistory referralHistory={referralHistory} /> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralEarnPage;
